import { useState, type ReactNode } from "react";

/**
 * Revela su contenido precedido por una pantalla teaser de tensión.
 *
 * Réplica web del `DramaticReveal` de iOS. Estado inicial: un overlay con
 * copy dramático y un botón "Reveal". Al pulsarlo, el teaser se desvanece y
 * el contenido entra con un scale-up.
 *
 * Pensado para el modo `dramaticReveal`. El contenido se inyecta como
 * `children`.
 */
export interface DramaticRevealProps {
  children: ReactNode;
  teaser?: string;
  revealLabel?: string;
}

export default function DramaticReveal({
  children,
  teaser = "Something lovely awaits...",
  revealLabel = "Reveal"
}: DramaticRevealProps) {
  const [revealed, setRevealed] = useState(false);
  const [opening, setOpening] = useState(false);

  if (revealed) {
    return <>{children}</>;
  }

  const openCurtain = () => {
    setOpening(true);
    window.setTimeout(() => setRevealed(true), 980);
  };

  return (
    <div className={`dramatic-stage mx-auto max-w-md overflow-hidden rounded-[32px] text-center shadow-[0_30px_90px_-46px_rgba(64,12,26,0.8)] ${opening ? "is-opening" : ""}`}>
      <div className="dramatic-curtain dramatic-curtain-left" aria-hidden />
      <div className="dramatic-curtain dramatic-curtain-right" aria-hidden />
      <div className="dramatic-spotlight" aria-hidden />
      <div className="dramatic-sparkles" aria-hidden>
        <span />
        <span />
        <span />
      </div>
      <div className="relative z-10 flex flex-col items-center gap-5 px-8 py-11">
        <div className="dramatic-emblem" aria-hidden>✦</div>
        <p className="font-display text-3xl font-bold leading-tight text-white sm:text-4xl">{teaser}</p>
        <p className="max-w-xs text-sm font-semibold leading-6 text-white/72">
          The curtain is about to rise.
        </p>
      </div>
      <button
        type="button"
        onClick={openCurtain}
        disabled={opening}
        className="dramatic-button relative z-20 mb-8 min-w-[190px] rounded-pill px-7 py-3 text-base font-semibold text-white disabled:opacity-70"
      >
        {opening ? "Opening..." : revealLabel}
      </button>
    </div>
  );
}
